import React, { useMemo } from 'react';
import * as THREE from 'three';

// Vector colors
const COLORS = {
  source: '#5cead4',
  sourceDirection: '#98FAA3',
  detector: '#94a3b8',
  xray: '#fbbf24',
  origin: '#ffffff',
  rotationAxis: '#ffffff',
  uVector: '#FAC198',
  vVector: '#98FADB',
  normalVector: '#CA98FA',
};

/**
 * Renders a single source point as a sphere
 */
function SourcePoint({ position }) {
  return (
    <mesh position={position}>
      <sphereGeometry args={[0.5, 16, 16]} />
      <meshStandardMaterial color={COLORS.source} emissive={COLORS.source} emissiveIntensity={0.3} />
    </mesh>
  );
}

/**
 * Renders a detector plane as a rectangle
 */
function DetectorPlane({ position, uVec, vVec, width = 10, height = 10 }) {
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();

    // Scale vectors for visualization
    const halfU = uVec.map(v => v * width * 0.5);
    const halfV = vVec.map(v => v * height * 0.5);

    // Four corners of the detector
    const corners = [
      // Bottom-left
      [position[0] - halfU[0] - halfV[0], position[1] - halfU[1] - halfV[1], position[2] - halfU[2] - halfV[2]],
      // Bottom-right
      [position[0] + halfU[0] - halfV[0], position[1] + halfU[1] - halfV[1], position[2] + halfU[2] - halfV[2]],
      // Top-right
      [position[0] + halfU[0] + halfV[0], position[1] + halfU[1] + halfV[1], position[2] + halfU[2] + halfV[2]],
      // Top-left
      [position[0] - halfU[0] + halfV[0], position[1] - halfU[1] + halfV[1], position[2] - halfU[2] + halfV[2]],
    ];

    const vertices = new Float32Array([
      ...corners[0], ...corners[1], ...corners[2],
      ...corners[0], ...corners[2], ...corners[3],
    ]);

    geo.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geo.computeVertexNormals();

    return geo;
  }, [position, uVec, vVec, width, height]);

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial
        color={COLORS.detector}
        transparent
        opacity={0.4}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

/**
 * Renders an X-ray path from source to detector
 */
function XRayPath({ source, detector }) {
  const points = useMemo(() => {
    return [
      new THREE.Vector3(...source),
      new THREE.Vector3(...detector),
    ];
  }, [source, detector]);

  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(points);
  }, [points]);

  return (
    <line geometry={geometry}>
      <lineBasicMaterial color={COLORS.xray} transparent opacity={0.5} />
    </line>
  );
}

/**
 * Renders a direction vector arrow
 */
function DirectionArrow({ origin, direction, color, scale = 5 }) {
  const { lineGeometry, conePosition, coneRotation } = useMemo(() => {
    const dir = new THREE.Vector3(...direction).normalize();
    const end = new THREE.Vector3(...origin).add(dir.clone().multiplyScalar(scale));

    const lineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(...origin),
      end,
    ]);

    // Calculate rotation for the cone
    const up = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(up, dir);
    const euler = new THREE.Euler().setFromQuaternion(quaternion);

    return {
      lineGeometry: lineGeo,
      conePosition: [end.x, end.y, end.z],
      coneRotation: [euler.x, euler.y, euler.z],
    };
  }, [origin, direction, scale]);

  return (
    <group>
      <line geometry={lineGeometry}>
        <lineBasicMaterial color={color} />
      </line>
      <mesh position={conePosition} rotation={coneRotation}>
        <coneGeometry args={[0.3, 0.8, 8]} />
        <meshStandardMaterial color={color} />
      </mesh>
    </group>
  );
}

/**
 * Renders the sample origin point
 */
function SampleOrigin() {
  return (
    <mesh position={[0, 0, 0]}>
      <sphereGeometry args={[0.25, 32, 32]} />
      <meshStandardMaterial color={COLORS.origin} emissive={COLORS.origin} emissiveIntensity={0.5} />
    </mesh>
  );
}

/**
 * Renders the rotation axis
 */
function RotationAxis({ length = 50 }) {
  const points = useMemo(() => {
    return [
      new THREE.Vector3(0, 0, -length),
      new THREE.Vector3(0, 0, length),
    ];
  }, [length]);

  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(points);
  }, [points]);

  return (
    <line geometry={geometry}>
      <lineDashedMaterial
        color={COLORS.rotationAxis}
        dashSize={1}
        gapSize={0.5}
        transparent
        opacity={0.5}
      />
    </line>
  );
}

/**
 * Renders XYZ coordinate axes
 */
function CoordinateAxes({ size = 20, showX = true, showY = true, showZ = true }) {
  return (
    <group>
      {/* X axis - Red */}
      {showX && (
        <arrowHelper args={[
          new THREE.Vector3(1, 0, 0),
          new THREE.Vector3(0, 0, 0),
          size,
          0xff0000,
          1,
          0.5
        ]} />
      )}
      {/* Y axis - Green */}
      {showY && (
        <arrowHelper args={[
          new THREE.Vector3(0, 1, 0),
          new THREE.Vector3(0, 0, 0),
          size,
          0x00ff00,
          1,
          0.5
        ]} />
      )}
      {/* Z axis - Blue */}
      {showZ && (
        <arrowHelper args={[
          new THREE.Vector3(0, 0, 1),
          new THREE.Vector3(0, 0, 0),
          size,
          0x0000ff,
          1,
          0.5
        ]} />
      )}
    </group>
  );
}

/**
 * Main visualization component that renders all geometry vectors
 */
function VectorVisualization({
  vectors,
  detectorWidth = 10,
  detectorHeight = 10,
  showAxisX = true,
  showAxisY = true,
  showAxisZ = true,
  isAnimating = false,
  currentProjectionIndex = 0,
  showOrigin = true,
  showUVector = true,
  showVVector = true,
  showDirectionToSource = true,
  showDirectionToDetector = true
}) {
  if (!vectors || vectors.length === 0) {
    return null;
  }

  // Scale factor for visualization (to make vectors visible)
  const vectorScale = 3;

  // Determine which vectors to display
  const vectorsToDisplay = isAnimating ? [vectors[currentProjectionIndex]] : vectors;

  return (
    <group>
      {/* Static elements */}
      {showOrigin && <SampleOrigin />}
      <RotationAxis />
      <CoordinateAxes showX={showAxisX} showY={showAxisY} showZ={showAxisZ} />

      {/* Dynamic elements for each projection */}
      {vectorsToDisplay.map((v, i) => {
        const source = [v[0], v[1], v[2]];
        const detector = [v[3], v[4], v[5]];
        const uVec = [v[6], v[7], v[8]];
        const vVec = [v[9], v[10], v[11]];

        // Detector normal based on OD vector (Origin to Detector position)
        // Since origin is at [0,0,0], OD = detector position
        const normalVec = [detector[0], detector[1], detector[2]];

        // Source direction based on OS vector (Origin to Source position)
        // Since origin is at [0,0,0], OS = source position
        const sourceDir = [source[0], source[1], source[2]];

        return (
          <group key={i}>
            {/* Source point */}
            <SourcePoint position={source} />

            {/* Source direction vector (from origin to source) */}
            {showDirectionToSource && (
              <DirectionArrow
                origin={[0, 0, 0]}
                direction={sourceDir}
                color={COLORS.sourceDirection}
                scale={vectorScale}
              />
            )}

            {/* Detector plane */}
            <DetectorPlane
              position={detector}
              uVec={uVec}
              vVec={vVec}
              width={detectorWidth}
              height={detectorHeight}
            />

            {/* X-ray path from source to detector */}
            <XRayPath source={source} detector={detector} />

            {/* U direction vector (horizontal) */}
            {showUVector && (
              <DirectionArrow
                origin={detector}
                direction={uVec}
                color={COLORS.uVector}
                scale={vectorScale}
              />
            )}

            {/* V direction vector (vertical) */}
            {showVVector && (
              <DirectionArrow
                origin={detector}
                direction={vVec}
                color={COLORS.vVector}
                scale={vectorScale}
              />
            )}

            {/* Detector normal direction vector (from origin to detector) */}
            {showDirectionToDetector && (
              <DirectionArrow
                origin={[0, 0, 0]}
                direction={normalVec}
                color={COLORS.normalVector}
                scale={vectorScale}
              />
            )}
          </group>
        );
      })}
    </group>
  );
}

export default VectorVisualization;
